import { shouldIgnoreNavAnimation } from '@theme/navigation-ux';

/**
 * Press feedback for elements that navigate (product cards, links, etc.).
 * Sets `.is-interacting` on the navigation root for the duration of the press.
 */
const INTERACTING_CLASS = 'is-interacting';
const MIN_INTERACTION_MS = 80;

/** Controls that handle their own action — they become the interaction root. */
const CONTROL_SELECTOR = [
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '.button:not(:disabled)',
  '.button-cta:not(:disabled)',
  '.button-secondary:not(:disabled)',
  '.add-to-cart-button:not(:disabled)',
].join(', ');

/** @type {Element | null} */
let currentRoot = null;
/** @type {number | null} */
let clearTimeoutId = null;
/** @type {number} */
let interactionStartedAt = 0;

/**
 * @param {Event} event
 * @returns {Element[]}
 */
function getElementPath(event) {
  if (typeof event.composedPath === 'function') {
    return event.composedPath().filter((node) => node instanceof Element);
  }

  /** @type {Element[]} */
  const path = [];
  let node = event.target instanceof Element ? event.target : null;

  while (node) {
    path.push(node);
    node = node.parentElement;
  }

  return path;
}

/**
 * Returns the root element that owns the impending navigation.
 * @param {Event} event
 * @returns {Element | null}
 */
function getNavigationRoot(event) {
  if (event.target instanceof Element && shouldIgnoreNavAnimation(event.target)) {
    return null;
  }

  const path = getElementPath(event);

  for (const element of path) {
    if (shouldIgnoreNavAnimation(element)) {
      return null;
    }
  }

  for (const element of path) {
    if (element.matches(CONTROL_SELECTOR)) {
      return element;
    }
  }

  for (const element of path) {
    if (element.matches('product-card:not([data-no-navigation])')) {
      return element;
    }
  }

  for (const element of path) {
    if (element.matches('.resource-card, .collection-card')) {
      return element;
    }
  }

  for (const element of path) {
    if (element.matches('a[href]:not([href=""]):not([aria-disabled="true"])')) {
      if (element.classList.contains('product-card__link')) {
        return element.closest('product-card') ?? element;
      }

      if (element.classList.contains('resource-card__link')) {
        return element.closest('.resource-card') ?? element;
      }

      if (element.classList.contains('collection-card__link')) {
        return element.closest('.collection-card') ?? element;
      }

      return element;
    }
  }

  for (const element of path) {
    if (element.matches('.drawer-nav-item')) {
      return element;
    }
  }

  for (const element of path) {
    if (element.matches('summary.header__icon--menu, .header__icon--summary')) {
      return element;
    }
  }

  return null;
}

function clearInteracting() {
  if (clearTimeoutId) {
    window.clearTimeout(clearTimeoutId);
    clearTimeoutId = null;
  }

  if (!currentRoot) return;
  currentRoot.classList.remove(INTERACTING_CLASS);
  currentRoot = null;
  interactionStartedAt = 0;
}

function scheduleClearInteracting() {
  if (clearTimeoutId) {
    window.clearTimeout(clearTimeoutId);
  }

  const elapsed = Date.now() - interactionStartedAt;
  const delay = Math.max(0, MIN_INTERACTION_MS - elapsed);

  clearTimeoutId = window.setTimeout(() => {
    clearTimeoutId = null;
    clearInteracting();
  }, delay);
}

function setInteracting(/** @type {Element} */ root) {
  clearInteracting();
  root.classList.add(INTERACTING_CLASS);
  currentRoot = root;
  interactionStartedAt = Date.now();
}

/** @param {PointerEvent} event */
function handlePointerDown(event) {
  if (!(event instanceof PointerEvent) || event.button !== 0) return;

  const root = getNavigationRoot(event);
  if (!root) return;

  setInteracting(root);
}

document.addEventListener('pointerdown', handlePointerDown, { capture: true });
document.addEventListener('pointerup', scheduleClearInteracting, { capture: true });
document.addEventListener('pointercancel', scheduleClearInteracting, { capture: true });
document.addEventListener('click', scheduleClearInteracting, { capture: true });
document.addEventListener('lostpointercapture', scheduleClearInteracting, { capture: true });

document.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const root = getNavigationRoot(event);
    if (!root) return;

    setInteracting(root);
  },
  { capture: true }
);

document.addEventListener('keyup', (event) => {
  if (event.key === 'Enter' || event.key === ' ') scheduleClearInteracting();
});

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') clearInteracting();
});

window.addEventListener('pageshow', clearInteracting);
window.addEventListener('pagehide', clearInteracting);
